/**
 * Form Submit Handler Utility
 */

import { HiddenFields, ObjStrKeyStrVal } from '../types';

interface IpServiceResponse {
  ip: string;
}

/**
 * IP lookup service URLs with fallback.
 */
const IP_SERVICES = {
  primary: 'https://ipinfo.io/?token=bb318031a36257',
  fallback: 'https://api.ipify.org?format=json',
};

/**
 * Fetches client IP address with timeout and fallback service.
 *
 * @param timeout - Maximum time to wait for IP lookup in milliseconds
 * @returns Client IP address or 'Unavailable' if lookup fails
 */
const getClientIp = async (timeout = 5000): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Try primary service (ipinfo.io)
    const primaryResponse = await fetch(IP_SERVICES.primary, {
      signal: controller.signal,
    });

    if (primaryResponse.ok) {
      const data: IpServiceResponse = await primaryResponse.json();
      clearTimeout(timeoutId);
      return data.ip;
    }

    // Try fallback service (ipify)
    const fallbackResponse = await fetch(IP_SERVICES.fallback, {
      signal: controller.signal,
    });

    if (fallbackResponse.ok) {
      const data: IpServiceResponse = await fallbackResponse.json();
      clearTimeout(timeoutId);
      return data.ip;
    }
  } catch (error) {
    console.error('Error fetching IP:', error);
  }

  clearTimeout(timeoutId);
  return 'Unavailable';
};

/**
 * Redirects user to the specified URL.
 * Returns an unresolved promise to maintain the loading state on the form.
 *
 * @param redirectUrl - URL to redirect the user to
 * @returns Promise that never resolves (keeps spinner active during redirect)
 */
const redirectUser = (redirectUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
      // Promise intentionally never resolves to keep spinner active during redirect
    } else {
      console.error('No return URL configured for this form');
      reject(new Error('No redirect URL'));
    }
  });
};

/**
 * Handles form submission including IP lookup, payload preparation, and redirect.
 *
 * This function:
 * 1. Adds the HTTP referer to the payload
 * 2. Adds hidden fields to the payload
 * 3. Converts boolean values to strings (LPE requirement)
 * 4. Fetches the client IP address
 * 5. Submits the form data via POST
 * 6. Redirects user on successful submission
 *
 * @param submitParameters - Form field values from Formik
 * @param hiddenFields - Array of hidden field definitions
 * @param url - Form handler URL endpoint
 * @returns Promise that resolves after redirect or rejects on error
 *
 * @example
 * await submitHandler(values, hiddenFields, formHandlerUrl);
 */
export const submitHandler = async (
  submitParameters: ObjStrKeyStrVal,
  hiddenFields: HiddenFields[],
  url: string,
): Promise<void> => {
  const httpReferer = window.location.href;

  // Add referer
  submitParameters['httpReferer'] = httpReferer;

  // Add hidden fields
  for (const hidden of hiddenFields) {
    submitParameters[hidden.parameterName] = hidden.parameterValue;
  }

  // Convert booleans to strings (LPE backend requirement)
  for (const key in submitParameters) {
    const value = submitParameters[key];
    if (typeof value === 'boolean') {
      submitParameters[key] = String(value);
    }
  }

  // Get client IP
  submitParameters['ipAddress'] = await getClientIp(5000);

  // Prepare request
  const payload = { submitParameters };
  const headers = new Headers({
    'Content-Type': 'application/json',
    Accept: '*/*',
    Referer: httpReferer,
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (response.ok && response.status === 201) {
      const json = await response.json();
      const redirectUrl = json?.productionReturnUrl || httpReferer;
      return redirectUser(redirectUrl);
    }
  } catch (error) {
    console.error('Form submission error:', error);
  }
};
